import React, { useState, useEffect, useRef, useMemo } from "react";
import { IconMap } from "./IconMap";
import { ChevronDown } from "lucide-react";
import { useOptimizedResize } from '../utils/performance';

const sectionIcon = (icon) => typeof icon === 'string' && Object.prototype.hasOwnProperty.call(IconMap, icon)
    ? IconMap[icon]
    : null;

const CaseNavDropdown = ({ section, subSections, activeSection, setActiveSection, minimize, horizontal, isOpen, onToggle, caseType }) => {
    const isActive =
        activeSection === section.id ||
        subSections.some(sub => sub.id === activeSection);
    const dropdownRef = useRef(null);
    const [isChevronHover, setIsChevronHover] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (horizontal && isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onToggle(section.id);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [horizontal, isOpen, section.id, onToggle]);
    
    const handleToggle = (e) => {
        e.stopPropagation();
        onToggle(section.id);
    };
    
    const handleSubClick = (subSection, e) => {
        e.stopPropagation();
        setActiveSection(subSection.id);
    };

    // Only hide section 20000 when we have a defined caseType and it's not 6
    if (section.id === 20000 && caseType !== undefined && caseType !== 6) {
        return null;
    }
    
    return (
        <div className={`case-nav-dropdown ${isActive ? 'active' : ''}`} ref={dropdownRef}>
            <div 
                className={`case-nav-item subtext large ${isActive ? 'active' : ''} ${subSections.some(sub => sub.id === activeSection) ? 'main-active' : ''} ${section.name === "Activity Feed" ? 's1' : ''} ${isChevronHover ? 'chevron-hover' : ''}`} 
                onClick={(e) => {
                    e.stopPropagation();
                    setActiveSection(section.id, subSections);
                    if (subSections.length > 0) {
                        onToggle(section.id);
                    }
                }}
            >
                <span className="icon-container">{sectionIcon(section.icon) ? React.createElement(sectionIcon(section.icon)) : <></>}</span>
                {!minimize && (
                    <>
                        {!horizontal && <span className="section-name">{section.name}</span>}
                        {(subSections.length > 0 && !horizontal) && (
                            <div
                                className="menu-dropdown"
                                onClick={handleToggle}
                                onMouseEnter={() => setIsChevronHover(true)}
                                onMouseLeave={() => setIsChevronHover(false)}
                            >
                                <ChevronDown
                                    size={16}
                                    style={{
                                        display: !minimize ? "block" : "none",
                                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                        color: isOpen ? "var(--fill)" : "var(--text-color)",
                                    }}
                                    className='dropdown-arrow'
                                />    
                            </div>                    
                        )}
                    </>
                )}
            </div>
            
            {isOpen && subSections.length > 0 && (
                <div className={`case-nav-sub-items ${horizontal ? 'horizontal' : ''}`}>
                    {subSections.map((subSection, idx) => (
                        <div 
                            key={idx}
                            className={`case-submenu-item expanded subtext ${activeSection === subSection.id ? 'active' : ''}`}
                            onClick={(e) => handleSubClick(subSection, e)}
                            style={{ justifyContent: minimize ? 'center' : 'flex-start' }}
                        >
                            <span className="icon-container">{sectionIcon(subSection.icon) ? React.createElement(sectionIcon(subSection.icon)) : <></>}</span>
                            {!minimize && subSection.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const CaseNav = ({ sections, activeSection, setActiveSection, pfs, horizontal = false, caseType }) => {
    const safeSections = useMemo(() => Array.isArray(sections) ? sections.filter(section => (
        section !== null && typeof section === 'object' && !Array.isArray(section)
        && ((typeof section.id === 'number' && Number.isFinite(section.id))
            || (typeof section.id === 'string' && section.id.trim() !== ''))
        && typeof section.name === 'string'
    )) : [], [sections]);
    const [minimize, setMinimize] = useState(true);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const hoverTimeout = useRef(null);
    const suppressAutoOpenRef = useRef(false);
    const isStickySection = safeSections.some(
        (section) => section.id === activeSection &&
        (String(section.name).trim() === "Documents" || String(section.name).trim() === "Activity Feed")
    );

    const shouldMinimize = isStickySection || window.innerWidth <= 768;
    
    const handleDropdownToggle = (sectionId) => {
        setOpenDropdownId(prevId => {
            if (prevId === sectionId) {
                suppressAutoOpenRef.current = true;
                return null;
            }
            return sectionId;
        });
    };
    
    useOptimizedResize(() => {
        setMinimize(shouldMinimize);
    });

    const handleMinimize = () => {
        setMinimize(shouldMinimize);
    };

    useEffect(() => {
        setMinimize(shouldMinimize);
        if (shouldMinimize) {
            setOpenDropdownId(null);
        }
    }, [activeSection, safeSections, shouldMinimize]);

    const handleHover = () => {
        if (window.innerWidth > 768) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = setTimeout(() => {
                setMinimize(false);
            }, 500);
        }
    };
    
    const handleLeave = () => {
        clearTimeout(hoverTimeout.current);
        handleMinimize();
    };

    const organizedSections = (safeSections
        .filter(s => (s.name === "Uber PFS" && pfs) || s.id != 47)
        .reduce((acc, section) => {
            if (section.subsection === null) {
                acc.push({
                    ...section,
                    subSections: []
                });
            } else {
                const mainSection = acc.find(s => s.id === section.subsection);
                if (mainSection) {
                    mainSection.subSections.push(section);
                }
            }
            return acc;
        }, [])).slice();

    const documentsIndex = organizedSections.findIndex(
        (section) => String(section.name).trim().toLowerCase() === 'documents'
    );
    let documentsSection = null;
    if (documentsIndex > -1) {
        [documentsSection] = organizedSections.splice(documentsIndex, 1);
    }

    const vitalsIndex = organizedSections.findIndex(
        (section) => String(section.name).trim().toLowerCase() === 'vitals'
    );
    let vitalsSection = null;
    if (vitalsIndex > -1) {
        [vitalsSection] = organizedSections.splice(vitalsIndex, 1);
    }

    if (vitalsSection) {
        organizedSections.push(vitalsSection);
    }
    if (documentsSection) {
        organizedSections.push(documentsSection);
    }

    useEffect(() => {
        if (minimize || window.innerWidth <= 768) {
            setOpenDropdownId(null);
            return;
        }

        if (suppressAutoOpenRef.current) {
            suppressAutoOpenRef.current = false;
            return;
        }

        const activeParent = organizedSections.find(
            section => section.id === activeSection || section.subSections.some(sub => sub.id === activeSection)
        );

        if (!activeParent || !activeParent.subSections.length) {
            return;
        }

        if (openDropdownId !== null) {
            return;
        }

        setOpenDropdownId(activeParent.id);
    }, [activeSection, organizedSections, isStickySection, minimize, openDropdownId]);

    return (
        <div
            className={`case-nav ${horizontal ? 'horizontal' : ''} ${minimize ? 'minimized' : ''}`}
            onMouseOver={handleHover}
            onMouseLeave={handleLeave}
            onClick={() => {
                if (!shouldMinimize) {
                    setMinimize(false);
                }
            }}
        >
            {organizedSections.map((section, index) => (
                <CaseNavDropdown
                    key={index}
                    section={section}
                    subSections={section.subSections}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    minimize={minimize}
                    horizontal={horizontal}
                    isOpen={openDropdownId === section.id}
                    onToggle={handleDropdownToggle}
                    caseType={caseType}
                />
            ))}
        </div>
    );
};
